import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'deliveryField', 'pickupHint', 'paymentInput', 'fulfillmentInput', 'paymentSummary',
    'codWarning', 'submitButton', 'coinsSection', 'coinsToggle', 'coinsInput', 'coinsSummary',
    'coinsDiscountLabel', 'coinsRedeemedLabel', 'coinsTotalRow', 'coinsTotalDiscount',
    'walletTotalRow', 'walletCoinsLabel', 'orderTotalLabel', 'promoInput', 'promoButton',
    'promoError', 'promoApplied', 'promoCodeLabel', 'promoAppliesToLabel', 'promoDiscountLabel',
    'promoTotalRow', 'promoTotalDiscount', 'deliveryFeeRow', 'deliveryFeeLabel',
    'deliveryZoneInput', 'codOption', 'outOfCityCodNote', 'codRulesNote'
  ]
  static values = {
    codAllowed: Boolean,
    outOfCityCodAllowed: { type: Boolean, default: true },
    advancedCodRules: { type: Boolean, default: false },
    codMinOrderCents: { type: Number, default: 0 },
    codMaxOrderCents: { type: Number, default: 0 },
    codNewBuyerMaxCents: { type: Number, default: 0 },
    codBlockNewBuyers: { type: Boolean, default: false },
    buyerIsNew: { type: Boolean, default: false },
    platformNewBuyer: { type: Boolean, default: false },
    advancePaymentAvailable: { type: Boolean, default: false },
    phonePresent: { type: Boolean, default: true },
    totalCents: Number,
    deliveryFeeCents: { type: Number, default: 0 },
    freeDeliveryThresholdCents: { type: Number, default: 0 },
    riderFeeCents: { type: Number, default: 0 },
    coinsPerRupee: Number,
    maxCoins: Number,
    coinDiscountCents: Number,
    walletAllowed: Boolean,
    walletCoinsRequired: Number,
    promoPreviewUrl: String
  }

  connect () {
    this.baseMaxCoins = this.maxCoinsValue
    this.baseCoinDiscountCents = this.coinDiscountCentsValue
    this.baseWalletCoinsRequired = this.walletCoinsRequiredValue
    this.appliedPromoDiscountCents = 0
    this.appliedPromoCode = ''
    this.sync()
    this.syncCoins()
  }

  sync () {
    const fulfillment = this.selectedFulfillment()
    const isDelivery = this.isDeliveryFulfillment(fulfillment)
    const zone = this.selectedDeliveryZone()
    const zoneBlocksCod = isDelivery &&
      fulfillment === 'shop_delivery' &&
      zone === 'out_of_city' &&
      !this.outOfCityCodAllowedValue
    const rulesReason = this.advancedCodRulesReason()
    const rulesBlockCod = rulesReason != null
    const riderBlocksCod = fulfillment === 'ebazzari_rider' && this.platformNewBuyerValue
    const codBlocked = zoneBlocksCod || rulesBlockCod || riderBlocksCod

    this.syncCodAvailability(codBlocked)

    // New buyers on Rider with COD-only shops: fall back to a fulfillment that still allows COD.
    if (codBlocked && riderBlocksCod && !this.selectedPayment()) {
      const safer = this.fulfillmentInputTargets.find((input) => {
        if (input.disabled) return false
        return input.value === 'shop_delivery' || input.value === 'pickup'
      })
      if (safer && !safer.checked) {
        safer.checked = true
        this.sync()
        return
      }
    }

    const payment = this.selectedPayment()
    const isCod = payment === 'cod'
    const isWallet = payment === 'wallet'

    if (this.hasDeliveryFieldTarget) {
      this.deliveryFieldTarget.classList.toggle('hidden', !isDelivery)
    }
    if (this.hasPickupHintTarget) {
      this.pickupHintTarget.classList.toggle('hidden', isDelivery)
    }
    if (this.hasDeliveryFeeRowTarget) {
      this.deliveryFeeRowTarget.classList.toggle('hidden', !isDelivery)
      this.deliveryFeeRowTarget.classList.toggle('flex', isDelivery)
    }
    if (this.hasOutOfCityCodNoteTarget) {
      this.outOfCityCodNoteTarget.classList.toggle('hidden', !zoneBlocksCod)
    }
    if (this.hasCodRulesNoteTarget) {
      let note = rulesReason || ''
      if (riderBlocksCod) {
        note = this.selectedPayment()
          ? 'First orders with Ebazzari Rider need advance payment. Use shop delivery for COD, or pay advance with Rider.'
          : 'First orders with Ebazzari Rider need advance payment. This shop only has COD — choose shop delivery or pickup instead.'
      }
      this.codRulesNoteTarget.textContent = note
      this.codRulesNoteTarget.classList.toggle('hidden', !(rulesBlockCod || riderBlocksCod) || !note)
    }
    if (this.hasCodWarningTarget) {
      this.codWarningTarget.classList.toggle('hidden', !isCod || this.codAllowedValue)
    }
    if (this.hasCoinsSectionTarget) {
      this.coinsSectionTarget.classList.toggle('hidden', isWallet)
    }
    if (isWallet && this.hasCoinsToggleTarget && this.coinsToggleTarget.checked) {
      this.coinsToggleTarget.checked = false
    }
    if (this.hasPaymentSummaryTarget) {
      this.paymentSummaryTarget.textContent = this.paymentSummaryText(payment)
    }
    if (this.hasSubmitButtonTarget) {
      const blockedByCod = isCod && (!this.codAllowedValue || codBlocked)
      const noPayment = !payment
      this.submitButtonTarget.disabled = blockedByCod || noPayment || !this.phonePresentValue
      this.submitButtonTarget.value = this.submitLabel(payment)
    }

    if (isWallet) {
      this.showWalletTotals()
    } else {
      this.syncCoins()
    }
  }

  advancedCodRulesReason () {
    if (!this.advancedCodRulesValue) return null

    const amount = Number(this.totalCentsValue || 0)
    const minCents = Number(this.codMinOrderCentsValue || 0)
    const maxCents = Number(this.codMaxOrderCentsValue || 0)
    const hasAdvance = this.advancePaymentAvailableValue

    if (minCents > 0 && amount < minCents) {
      return hasAdvance
        ? `COD starts from ${this.formatPkr(minCents)}. Add more items or choose advance payment.`
        : `COD starts from ${this.formatPkr(minCents)}. Add more items to unlock COD.`
    }
    if (maxCents > 0 && amount > maxCents) {
      return hasAdvance
        ? `COD is limited to ${this.formatPkr(maxCents)}. Choose advance payment for larger orders.`
        : `COD is limited to ${this.formatPkr(maxCents)}. Reduce your cart or contact the shop.`
    }
    if (!this.buyerIsNewValue) return null

    if (this.codBlockNewBuyersValue) {
      return hasAdvance
        ? 'First-time buyers at this shop need advance payment (COD off for new buyers).'
        : 'First-time buyers cannot use COD at this shop yet. Contact the shop to arrange payment.'
    }

    const newMax = Number(this.codNewBuyerMaxCentsValue || 0)
    if (newMax > 0 && amount > newMax) {
      return hasAdvance
        ? `First-time buyers can use COD up to ${this.formatPkr(newMax)}. Choose advance payment or a smaller cart.`
        : `First-time buyers can use COD up to ${this.formatPkr(newMax)}. Reduce your cart to continue.`
    }

    return null
  }

  syncCodAvailability (codBlocked) {
    this.codOptionTargets.forEach((option) => {
      const input = option.querySelector('input[type="radio"]')
      if (!input) return

      const accountBlocked = !this.codAllowedValue
      const blocked = accountBlocked || codBlocked
      // Uncheck before disabling so a blocked COD radio is never the only "selected"
      // value (disabled fields are omitted from form submit).
      if (blocked && input.checked) {
        input.checked = false
        const fallback = this.paymentInputTargets.find(
          (candidate) => candidate !== input && !candidate.disabled && candidate.value !== 'cod'
        )
        if (fallback) fallback.checked = true
      }

      input.disabled = blocked
      option.classList.toggle('opacity-55', blocked)
      option.classList.toggle('cursor-not-allowed', blocked)
      option.classList.toggle('cursor-pointer', !blocked)
      option.classList.toggle('hidden', !!codBlocked)
    })
  }

  selectedDeliveryZone () {
    if (!this.hasDeliveryZoneInputTarget) return 'local'
    const selected = this.deliveryZoneInputTargets.find((input) => input.checked)
    return selected?.value || 'local'
  }

  syncCoins () {
    if (this.selectedPayment() === 'wallet') return

    const enabled = this.hasCoinsToggleTarget && this.coinsToggleTarget.checked
    const discountCents = enabled ? this.coinDiscountCentsValue : 0
    const shippingCents = this.currentShippingCents()
    const total = Math.max(this.totalCentsValue - this.appliedPromoDiscountCents - discountCents, 0) + shippingCents

    this.syncPromoTotals()
    this.syncDeliveryFeeLabel()
    if (this.hasCoinsSummaryTarget) {
      this.coinsSummaryTarget.classList.toggle('hidden', !enabled)
    }
    if (this.hasCoinsTotalRowTarget) {
      this.coinsTotalRowTarget.classList.toggle('hidden', !enabled)
    }
    if (this.hasWalletTotalRowTarget) {
      this.walletTotalRowTarget.classList.add('hidden')
    }
    if (this.hasCoinsInputTarget && enabled) {
      this.coinsInputTarget.value = this.maxCoinsValue
    }
    if (this.hasCoinsTotalDiscountTarget && enabled) {
      this.coinsTotalDiscountTarget.textContent = `−${this.formatPkr(discountCents)}`
    }
    if (this.hasCoinsDiscountLabelTarget && enabled) {
      this.coinsDiscountLabelTarget.textContent = this.formatPkr(discountCents)
    }
    if (this.hasCoinsRedeemedLabelTarget && enabled) {
      this.coinsRedeemedLabelTarget.textContent = this.formatCoins(this.maxCoinsValue)
    }
    if (this.hasOrderTotalLabelTarget) {
      this.orderTotalLabelTarget.textContent = this.formatPkr(total)
    }
  }

  showWalletTotals () {
    this.syncPromoTotals()
    this.syncDeliveryFeeLabel()
    if (this.hasCoinsSummaryTarget) {
      this.coinsSummaryTarget.classList.add('hidden')
    }
    if (this.hasCoinsTotalRowTarget) {
      this.coinsTotalRowTarget.classList.add('hidden')
    }
    if (this.hasWalletTotalRowTarget) {
      this.walletTotalRowTarget.classList.remove('hidden')
    }
    if (this.hasWalletCoinsLabelTarget) {
      this.walletCoinsLabelTarget.textContent = `${this.formatCoins(this.walletCoinsRequiredValue)} coins`
    }
    if (this.hasOrderTotalLabelTarget) {
      const shippingCents = this.currentShippingCents()
      this.orderTotalLabelTarget.textContent = this.formatPkr(shippingCents)
    }
    if (this.hasCoinsInputTarget) {
      this.coinsInputTarget.value = 0
    }
  }

  syncDeliveryFeeLabel () {
    if (!this.hasDeliveryFeeLabelTarget) return

    const fee = this.currentShippingCents()
    if (fee > 0) {
      this.deliveryFeeLabelTarget.textContent = this.formatPkr(fee)
    } else {
      this.deliveryFeeLabelTarget.textContent = 'Free'
    }
  }

  isDeliveryFulfillment (value = this.selectedFulfillment()) {
    return value === 'delivery' || value === 'shop_delivery' || value === 'ebazzari_rider'
  }

  currentShippingCents () {
    const fulfillment = this.selectedFulfillment()
    if (!this.isDeliveryFulfillment(fulfillment)) return 0
    if (fulfillment === 'ebazzari_rider') return this.riderFeeCentsValue

    const threshold = this.freeDeliveryThresholdCentsValue
    if (threshold > 0 && this.totalCentsValue >= threshold) return 0
    return this.deliveryFeeCentsValue
  }

  selectedFulfillment () {
    const selected = this.fulfillmentInputTargets.find((input) => input.checked)
    return selected?.value
  }

  selectedPayment () {
    const selected = this.paymentInputTargets.find((input) => input.checked)
    return selected?.value
  }

  applyPromoOnEnter (event) {
    if (event.key !== 'Enter') return

    event.preventDefault()
    this.applyPromo()
  }

  async applyPromo () {
    if (!this.hasPromoInputTarget || !this.hasPromoPreviewUrlValue) return

    const code = this.promoInputTarget.value.trim().toUpperCase()
    if (!code) {
      this.showPromoError('Enter a promo code.')
      return
    }

    this.setPromoLoading(true)

    try {
      const response = await fetch(this.promoPreviewUrlValue, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken()
        },
        body: JSON.stringify({ promo_code: code })
      })
      const payload = await response.json()

      if (!response.ok || !payload.ok) {
        throw new Error(payload.error || 'Promo code could not be applied.')
      }

      this.appliedPromoCode = payload.code || code
      this.appliedPromoDiscountCents = Number(payload.discount_cents || 0)
      this.coinDiscountCentsValue = Number(payload.coin_discount_cents || 0)
      this.maxCoinsValue = Number(payload.max_coins || 0)
      this.walletCoinsRequiredValue = Number(
        payload.wallet_coins_required === undefined ? this.baseWalletCoinsRequired : payload.wallet_coins_required
      )
      this.promoInputTarget.value = this.appliedPromoCode
      this.showPromoApplied(payload)
      this.sync()
    } catch (error) {
      this.resetPromoTotals()
      this.showPromoError(error.message)
    } finally {
      this.setPromoLoading(false)
    }
  }

  clearPromoPreview () {
    if (this.hasPromoErrorTarget) {
      this.promoErrorTarget.classList.add('hidden')
      this.promoErrorTarget.textContent = ''
    }
    if (!this.appliedPromoCode) return

    this.resetPromoTotals()
    this.sync()
  }

  showPromoApplied (payload) {
    if (this.hasPromoErrorTarget) {
      this.promoErrorTarget.classList.add('hidden')
      this.promoErrorTarget.textContent = ''
    }
    if (this.hasPromoAppliedTarget) {
      this.promoAppliedTarget.classList.remove('hidden')
    }
    if (this.hasPromoCodeLabelTarget) {
      this.promoCodeLabelTarget.textContent = this.appliedPromoCode
    }
    if (this.hasPromoAppliesToLabelTarget) {
      this.promoAppliesToLabelTarget.textContent = payload.applies_to || 'this order'
    }
    if (this.hasPromoDiscountLabelTarget) {
      this.promoDiscountLabelTarget.textContent = `Saved ${this.formatPkr(this.appliedPromoDiscountCents)}`
    }
  }

  showPromoError (message) {
    if (this.hasPromoAppliedTarget) {
      this.promoAppliedTarget.classList.add('hidden')
    }
    if (this.hasPromoErrorTarget) {
      this.promoErrorTarget.textContent = message
      this.promoErrorTarget.classList.remove('hidden')
    }
  }

  syncPromoTotals () {
    const enabled = this.appliedPromoDiscountCents > 0
    if (this.hasPromoTotalRowTarget) {
      this.promoTotalRowTarget.classList.toggle('hidden', !enabled)
    }
    if (this.hasPromoTotalDiscountTarget) {
      this.promoTotalDiscountTarget.textContent = `−${this.formatPkr(this.appliedPromoDiscountCents)}`
    }
  }

  resetPromoTotals () {
    this.appliedPromoCode = ''
    this.appliedPromoDiscountCents = 0
    this.coinDiscountCentsValue = this.baseCoinDiscountCents
    this.maxCoinsValue = this.baseMaxCoins
    this.walletCoinsRequiredValue = this.baseWalletCoinsRequired

    if (this.hasPromoAppliedTarget) {
      this.promoAppliedTarget.classList.add('hidden')
    }
    if (this.hasPromoTotalRowTarget) {
      this.promoTotalRowTarget.classList.add('hidden')
    }
    if (this.hasPromoErrorTarget) {
      this.promoErrorTarget.classList.add('hidden')
      this.promoErrorTarget.textContent = ''
    }
  }

  setPromoLoading (loading) {
    if (!this.hasPromoButtonTarget) return

    this.promoButtonTarget.disabled = loading
    this.promoButtonTarget.textContent = loading ? 'Checking' : 'Apply'
  }

  csrfToken () {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }

  paymentSummaryText (payment) {
    switch (payment) {
      case 'wallet':
        return 'Paid fully with Ebazzari Coins.'
      case 'pkr_wallet':
        return 'Total charged from your Cash Wallet.'
      case 'half_advance':
        return '50% now, 50% on delivery or pickup.'
      case 'full_advance':
        return 'Full amount paid before preparation.'
      default:
        return 'Pay the full amount on delivery.'
    }
  }

  submitLabel (payment) {
    switch (payment) {
      case 'wallet':
        return 'Place order · pay with Coins'
      case 'pkr_wallet':
        return 'Place order · pay with Cash Wallet'
      case 'half_advance':
        return 'Place order · half payment upfront'
      case 'full_advance':
        return 'Place order · pay in advance'
      default:
        return 'Place order · pay on delivery'
    }
  }

  formatPkr (cents) {
    const rupees = (cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)
    return `Rs ${Number(rupees).toLocaleString()}`
  }

  formatCoins (amount) {
    return Number(amount).toLocaleString()
  }
}
